import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { fetchCategories, fetchTools } from "../lib/api";
import ToolCard from "../components/ToolCard";
import Seo from "../components/Seo";
import { getCategoryStyle } from "../lib/categoryStyle";

export default function CategoryPage() {
    const { slug } = useParams();
    const [category, setCategory] = useState(null);
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [cats, ts] = await Promise.all([
                    fetchCategories(),
                    fetchTools({ category: slug }),
                ]);
                const cat = cats.find((c) => c.slug === slug);
                setCategory(cat);
                setTools(ts);
            } finally {
                setLoading(false);
            }
        })();
    }, [slug]);

    const style = getCategoryStyle(category?.color || "primary");

    return (
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-10 pb-16">
            {category && (
                <Seo
                    path={`/category/${category.slug}`}
                    title={`${category.name} — Free AI Tools (No Sign-up)`}
                    description={`${category.tagline} ${tools.length} free AI tools in the ${category.name} category. Unlimited, no login required.`}
                />
            )}
            <Link
                to="/"
                data-testid="back-to-home"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:underline underline-offset-4 decoration-2"
            >
                <ArrowLeft className="w-4 h-4" strokeWidth={3} />
                All categories
            </Link>

            {category && (
                <section className={`mt-6 nb-border-4 nb-shadow-lg rounded-3xl ${style.bg} p-8 md:p-12`}>
                    <div className="text-xs font-bold uppercase tracking-widest text-black/70">
                        {tools.length} tools in this category
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter mt-2">
                        {category.name}
                    </h1>
                    <p className="mt-3 text-lg font-medium max-w-2xl">{category.tagline}</p>
                </section>
            )}

            {loading ? (
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div
                            key={i}
                            className="nb-border rounded-2xl bg-white p-5 h-48 animate-pulse"
                        />
                    ))}
                </div>
            ) : (
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                    {tools.map((t) => (
                        <ToolCard key={t.slug} tool={t} />
                    ))}
                </div>
            )}
        </div>
    );
}
